import { Injectable } from '@angular/core';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class UserInteractionService {
  constructor(private firebaseService: FirebaseService, private userService: UserService) { }

  private async getCurrentUser() {
    return await this.userService.getUserProfile();
  }

  async blockUser(otherUserId: string, connectionId: string, reason: string): Promise<void> {
    const currentUser = await this.getCurrentUser();

    // Data for the user being blocked
    const blockedUserData = {
      blockedBy: currentUser.id,
      connectionId,
      reason,
      createdAt: new Date()
    };

    // Data for the user who initiated the block
    const blockingUserData = {
      connectionId,
      userBlocked: otherUserId,
      createdAt: new Date()
    };

    const blockingUserRef = doc(collection(this.firebaseService.firestore, `users/${currentUser.id}/blocked`));
    await setDoc(blockingUserRef, blockingUserData);

    const blockedUserRef = doc(collection(this.firebaseService.firestore, `users/${otherUserId}/blocks`));
    await setDoc(blockedUserRef, blockedUserData);

    const globalRef = doc(collection(this.firebaseService.firestore, 'user-block-records'));
    await setDoc(globalRef, {
      ...blockedUserData,
      blockedUser: otherUserId
    });
  }

  async isUserBlockedBy(otherUserId: string): Promise<boolean> {
    const currentUser = await this.getCurrentUser();
    const blocksRef = collection(this.firebaseService.firestore, `users/${currentUser.id}/blocks`);
    const q = query(blocksRef, where('blockedBy', '==', otherUserId));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  async hasCurrentUserBlocked(otherUserId: string): Promise<boolean> {
    const currentUser = await this.getCurrentUser();
    const blockedRef = collection(this.firebaseService.firestore, `users/${currentUser.id}/blocked`);
    const q = query(blockedRef, where('userBlocked', '==', otherUserId));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }


  async reviewUser(otherUserId: string, connectionId: string, rating: number, feedback: string): Promise<void> {
    const user = await this.getCurrentUser();
    const reviewData = {
      reviewedBy: user.id,
      connectionId,
      rating,
      feedback: feedback || null,
      createdAt: new Date()
    };

    const userReviewRef = doc(collection(this.firebaseService.firestore, `users/${otherUserId}/reviews`));
    await setDoc(userReviewRef, reviewData);

    const globalRef = doc(collection(this.firebaseService.firestore, 'user-review-records'));
    await setDoc(globalRef, { ...reviewData, reviewedUser: otherUserId });
  }

  async hasUserBeenReviewed(otherUserId: string, connectionId: string): Promise<boolean> {
    const user = await this.getCurrentUser();
    const reviewsRef = collection(this.firebaseService.firestore, `users/${otherUserId}/reviews`);
    const q = query(
      reviewsRef,
      where('reviewedBy', '==', user.id),
      where('connectionId', '==', connectionId)
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }
}
